import { HorizontalRuleNode } from '@lexical/extension';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';

import type { EditorFeature } from '../core/context/FeaturesContext';
import type { EditorFeatures, LexicalProps } from '../types';
import { CharacterLimitPlugin } from './character-limit/CharacterLimitPlugin';
import { CollaborationPlugin } from './collaboration/CollaborationPlugin';
import { DynamicDataNode } from './dynamic-data/DynamicDataNode';
import { DynamicDataPlugin } from './dynamic-data/DynamicDataPlugin';
import { HorizontalRulePlugin } from './horizontal-rule/HorizontalRulePlugin';
import { ImageNode } from './images/ImageNode';
import { ImagesPlugin } from './images/ImagesPlugin';
import { LinksPlugin } from './links/LinksPlugin';
import { ListNumberingPlugin } from './list-numbering/ListNumberingPlugin';
import { MentionNode } from './mentions/MentionNode';
import { MentionsPlugin } from './mentions/MentionsPlugin';
import { MultiLanguageProvider } from './multi-language/MultiLanguageContext';
import { RichTextPlugins } from './rich-text/RichTextPlugins';
import { SingleLinePlugin } from './single-line/SingleLinePlugin';
import { SubmitPlugin } from './submit/SubmitPlugin';
import { TablePlugin } from './tables/TablePlugin';

const isRich = (props: LexicalProps) => props.mode === 'rich';
const isOn = (key: keyof EditorFeatures) => (props: LexicalProps) => !!props.features?.[key]?.enabled;

/**
 * Every editor capability, and the only place deciding when each is on.
 * - Rich-text features are built in (always on in rich mode); the rest follow `props.features`.
 * - Order is render order: providers wrap outermost-first, plugins mount top to bottom.
 * - To add a feature: see the checklist in `README.md`.
 */
export const EDITOR_FEATURES: readonly EditorFeature[] = [
  { name: 'multiLanguage', isEnabled: isOn('multiLanguage'), Provider: MultiLanguageProvider },
  { name: 'richText', isEnabled: isRich, nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode], Plugin: RichTextPlugins },
  { name: 'listNumbering', isEnabled: isRich, Plugin: ListNumberingPlugin },
  { name: 'links', isEnabled: isRich, nodes: [LinkNode, AutoLinkNode], Plugin: LinksPlugin },
  { name: 'horizontalRule', isEnabled: isRich, nodes: [HorizontalRuleNode], Plugin: HorizontalRulePlugin },
  { name: 'tables', isEnabled: isRich, nodes: [TableNode, TableRowNode, TableCellNode], Plugin: TablePlugin },
  // On in rich mode unless a host opts out with `features.images.enabled: false`
  { name: 'images', isEnabled: (props) => isRich(props) && props.features?.images?.enabled !== false, nodes: [ImageNode], Plugin: ImagesPlugin },
  { name: 'collaboration', isEnabled: isOn('collaboration'), Plugin: CollaborationPlugin },
  // Yjs' UndoManager replaces it while collaborating; running both corrupts the undo stack
  { name: 'history', isEnabled: (props) => !props.features?.collaboration?.enabled, Plugin: HistoryPlugin },
  { name: 'singleLine', isEnabled: isOn('singleLine'), Plugin: SingleLinePlugin },
  // Counted as plain text, so rich values (HTML) don't take part
  {
    name: 'characterLimit',
    isEnabled: (props) => !isRich(props) && isOn('characterLimit')(props) && (props.features?.characterLimit?.max ?? 0) > 0,
    Plugin: CharacterLimitPlugin,
  },
  { name: 'dynamicData', isEnabled: isOn('dynamicData'), nodes: [DynamicDataNode], Plugin: DynamicDataPlugin },
  { name: 'mentions', isEnabled: isOn('mentions'), nodes: [MentionNode], Plugin: MentionsPlugin },
  // The X is rendered by `ContentArea`; there's nothing to mount
  { name: 'clearInput', isEnabled: isOn('clearInput') },
  // After `ValuePlugin` (mounted before every feature), whose first load clears the selection
  { name: 'autoFocus', isEnabled: (props) => !!props.autoFocus && !props.disabled, Plugin: AutoFocusPlugin },
  // Always on: Ctrl+Enter, and Enter in single-line mode, deactivate the editor
  { name: 'submit', isEnabled: () => true, Plugin: SubmitPlugin },
];

/** All nodes any feature uses; registered on every editor, since the composer config can't change after mount. */
export const EDITOR_NODES = [...new Set(EDITOR_FEATURES.flatMap(({ nodes = [] }) => nodes))];
